import React, { useEffect, useRef, useState } from "react";
import styles from "./Attorney.module.scss";
import {
  ApiPaginationResponse,
  AttorneyDataType,
} from "../../../interface/site.interface";
import { AttorneyService } from "../../../services/attornary.service";
import InputComponent from "../../../components/shared/input/InputComponent";
import SelectComponent from "../../../components/shared/select/SelectComponent";
import MainButton from "../../../components/shared/button/MainButton";
import PhoneInputComponent from "../../../components/shared/input-phone-component/PhoneInputComponent";
import OrderRow from "../../../components/pages/courier/OrderRow";
import GlobalModalComponent from "../../../components/shared/globalModal/GlobalModalComponent";
import Spinner from "../../../components/shared/spinner/Spinner";
import Card from "../../../components/pages/card/Card";
import CloseIcon from "../../../assets/images/referal/Close Icon.svg";
import CalendarImg from "../../../assets/images/packages/CalendarDots.svg";
import TitleUpdater from "../../../components/shared/title/TitleUpdater";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchTranslation } from "../../../redux/Slices/translationDataSlice";
import ArrowImage from "../../../assets/images/pages/login/CaretLeft.svg";
import { toast } from "react-toastify";

export interface AttorneyFormValues {
  name: string;
  surname: string;
  patronymic: string;
  passport_series: string;
  passport_number: string;
  passport_fin: string;
  birthday: string;
  phone: string;
  phone_prefix: string;
}

const Attorney = () => {
  const [formValues, setFormValues] = useState<AttorneyFormValues>({
    name: "",
    surname: "",
    patronymic: "",
    passport_series: "",
    passport_number: "",
    passport_fin: "",
    birthday: "",
    phone: "",
    phone_prefix: "",
  });
  const [editFormValues, setEditFormValues] = useState<AttorneyFormValues>({
    name: "",
    surname: "",
    patronymic: "",
    passport_series: "",
    passport_number: "",
    passport_fin: "",
    birthday: "",
    phone: "",
    phone_prefix: "",
  });
  const dispatch = useDispatch<AppDispatch>();

  const [attorney, setAttorney] = useState<AttorneyDataType[]>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAttorneyId, setSelectedAttorneyId] = useState<number | null>(
    null
  );
  const [phoneCode, setPhoneCode] = useState("50");
  const { data } = useSelector((state: RootState) => state.translationData);

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paginationData, setPaginationData] =
    useState<ApiPaginationResponse<AttorneyDataType> | null>(null);
  const language = useSelector((state: RootState) => state.language.language);

  const fetchAttorney = () => {
    setLoading(true);
    AttorneyService.GetAttorney()
      .then((res) => {
        setAttorney(res.data.events.data);
        setPaginationData(res?.data.events);
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAttorney();
  }, [language]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      phone: e.target.value.replace(phoneCode, ""), // Remove the phone code when updating the phone number
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const payload = {
      ...formValues,
      phone: formValues.phone,
      phone_prefix: phoneCode,
    };
    AttorneyService.PostAttornary(payload)
      .then(() => {
        toast.success(data?.authorization_added);
        fetchAttorney();
        setFormValues({
          name: "",
          surname: "",
          patronymic: "",
          passport_series: "",
          passport_number: "",
          passport_fin: "",
          birthday: "",
          phone: "",
          phone_prefix: "",
        });
      })
      .catch((error: any) => {
        setErrors(error.content);
      })
      .finally(() => {
        setIsSubmitting(false); // İşlem bitti, butonu tekrar aktif et
      });
  };

  const handleDeleteAttorney = (id: number) => {
    setSelectedAttorneyId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedAttorneyId) {
      AttorneyService.DeleteAttornary(selectedAttorneyId)
        .then(() => {
          toast.success(data?.authorization_deleted);
          fetchAttorney();
          setDeleteModalOpen(false);
        })
        .catch((error) => {
          setDeleteModalOpen(false);
        });
    }
  };

  const handleEditAttorney = (attorney: AttorneyDataType) => {
    setSelectedAttorneyId(attorney.id);
    setEditFormValues({
      name: attorney.name,
      surname: attorney.surname,
      patronymic: attorney.patronymic,
      passport_series: attorney.passport_series,
      passport_number: attorney.passport_number,
      passport_fin: attorney.passport_fin,
      birthday: attorney.birthday,
      phone: attorney.phone || "50",
      phone_prefix: attorney.phone_prefix || "50",
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAttorneyId) return;
    AttorneyService.UpdateAttornary(selectedAttorneyId, editFormValues)
      .then(() => {
        toast.success(data?.authorization_updated);
        fetchAttorney();
        setEditModalOpen(false);
      })
      .catch((error: any) => {
        setErrors(error.content);
      });
  };

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (!data) {
        dispatch(fetchTranslation());
      }
    } else {
      dispatch(fetchTranslation());
    }
  }, [dispatch, language]);

  const handlePageChange = async (urlOrPage: string | number) => {
    setLoading(true);
    try {
      let res;
      if (typeof urlOrPage === "string") {
        res = await AttorneyService.GetAttorney(urlOrPage);
      } else {
        const url = `/attorney?page=${urlOrPage}`;
        res = await AttorneyService.GetAttorney(url);
      }

      setAttorney(res.data.events.data);
      setPaginationData(res.data.events);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <TitleUpdater
        title={
          data?.colibri && data?.auth_left_sidebar_attorney
            ? `${data?.colibri} | ${data?.auth_left_sidebar_attorney}`
            : "Colibri"
        }
      />
      <form id={styles.account_purchase} onSubmit={handleSubmit}>
        <div className={styles.account_row}>
          <InputComponent
            label={data?.auth_attorney_name}
            type="text"
            name="name"
            placeholder={data?.auth_attorney_name}
            value={formValues.name}
            onChange={handleInputChange}
            error={errors.name}
          />
          <InputComponent
            label={data?.auth_attorney_surname}
            type="text"
            name="surname"
            placeholder={data?.auth_attorney_surname}
            value={formValues.surname}
            onChange={handleInputChange}
            error={errors.surname}
          />
          <InputComponent
            label={data?.auth_attorney_father_name}
            type="text"
            name="patronymic"
            placeholder={data?.auth_attorney_father_name}
            value={formValues.patronymic}
            onChange={handleInputChange}
            error={errors.patronymic}
          />

          <SelectComponent
            label={data?.auth_attorney_series_title}
            name="passport_series"
            options={[
              { value: "AA", label: "AA" },
              { value: "AZE", label: "AZE" },
            ]}
            value={formValues.passport_series}
            onChange={(value) => {
              setFormValues({
                ...formValues,
                passport_series: value.toString(),
              });
              setErrors({
                ...errors,
                passport_series: "", // Burada səhv mesajını təmizləyirik
              });
            }}
            placeholder={data?.auth_attorney_select}
            error={errors.passport_series}
          />
          <InputComponent
            label={data?.auth_attorney_series_number}
            type="number"
            name="passport_number"
            placeholder="0000000"
            value={formValues.passport_number}
            onChange={handleInputChange}
            error={errors.passport_number}
          />

          <InputComponent
            label={data?.auth_attorney_passport_fin}
            type="text"
            name="passport_fin"
            placeholder={data?.auth_attorney_passport_fin}
            value={formValues.passport_fin}
            onChange={handleInputChange}
            error={errors.passport_fin}
          />
          {/* <InputComponent
            label="Doğum tarixi"
            type="date"
            name="birthday"
            placeholder=""
            value={formValues.birthday}
            onChange={handleInputChange}
            error={errors.birthday}
          /> */}
          <div
            className={styles.dateinputWrapper}
            style={{ width: "100%", display: "flex", flexDirection: "column" }}
          >
            {data?.auth_attorney_birth_date}
            <div className={styles.dateInputWrapper}>
              <InputComponent
                label=""
                type="date"
                name="birthday"
                placeholder={data?.auth_attorney_birth_date}
                value={formValues.birthday}
                onChange={handleInputChange}
                // error={errors.birthday}
              />
              {/* <img
                src={CalendarImg}
                alt="calendar icon"
                className={styles.dateIcon}
              /> */}
            </div>
            <p className={styles.errorMsg}> {errors.birthday}</p>
          </div>

          <PhoneInputComponent
            onCodeChange={(code) => setPhoneCode(code)}
            label={data?.auth_attorney_mobile}
            id="phone"
            name="phone"
            placeholder="0000000"
            value={""}
            onChange={(e) => {
              setFormValues((prevValues) => ({
                ...prevValues,
                phone: e.target.value, // Telefon nömrəsini güncəlləyirik
              }));

              // Error mesajını silirik
              setErrors({
                ...errors,
                phone: "", // Telefon sahəsinin error mesajını təmizləyirik
              });
            }}
            error={errors.phone}
            combinePrefixWithNumber={false}
          />
        </div>
        <MainButton
          className={styles.mainButton}
          text={data?.auth_attorney_confirm}
          type="submit"
        />
      </form>

      <div className={styles.pagination_part}>
        <p className={styles.pagination_title}>
          {/* {data?.auth_courier_my_orders} */}
          Attorney list
        </p>
        {(paginationData?.current_page === 1 &&
          paginationData?.next_page_url === null) ||
          (paginationData && (
            <div className={styles.pagination}>
              <img
                src={ArrowImage}
                className={`${styles.pagination_arrow_left} ${
                  !paginationData.prev_page_url ? styles.disabled : ""
                }`}
                alt="Prev"
                onClick={() => {
                  if (paginationData.prev_page_url) {
                    handlePageChange(paginationData.prev_page_url);
                  }
                }}
              />

              {[...Array(paginationData.last_page)].map((_, index) => {
                const page = index + 1;
                return (
                  <p
                    key={page}
                    className={`${styles.pagination_count} ${
                      paginationData.current_page === page ? styles.active : ""
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </p>
                );
              })}

              <img
                src={ArrowImage}
                className={`${styles.pagination_arrow_right} ${
                  !paginationData.next_page_url ? styles.disabled : ""
                }`}
                alt="Next"
                onClick={() => {
                  if (paginationData.next_page_url) {
                    handlePageChange(paginationData.next_page_url);
                  }
                }}
              />
            </div>
          ))}
      </div>

      <div className={styles.table_Scroll}>
        {loading ? (
          <div className={styles.loadingWrapper}>
            <Spinner size="large" />
          </div>
        ) : attorney?.length !== 0 ? (
          <table className={styles.order_history_list}>
            <thead>
              <tr>
                {/* <th>Ad</th>
                <th>Soyad</th>
                <th>Ata adi</th>
                <th>Sənədin seriasi</th>
                <th>Sənədin nömrəsi</th>
                <th>FİN Kodu</th>
                <th>Doğum Tarixi</th>
                <th>Telefon Nömrəsi</th>
                <th>Düzəliş et</th> */}

                <th>{data?.auth_table_header_first_name}</th>
                <th>{data?.auth_table_header_last_name}</th>
                <th>{data?.auth_table_header_father_name}</th>
                <th>{data?.auth_table_header_document_series}</th>
                <th>{data?.auth_table_header_document_number}</th>
                <th>{data?.auth_table_header_fin_code}</th>
                <th>{data?.auth_table_header_birth_date}</th>
                <th>{data?.auth_table_header_phone}</th>
                <th>{data?.auth_table_header_edit}</th>
              </tr>
            </thead>
            <tbody>
              {attorney?.map((order, index) => (
                <OrderRow
                  key={index}
                  {...order}
                  attornary={true}
                  onDelete={() => handleDeleteAttorney(order.id)}
                  onEdit={() => handleEditAttorney(order)}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <div className={styles.no_attorney}>
            <p>{data?.auth_common_no_attorneys}</p>
          </div>
        )}
      </div>
      <div className={styles.courier_card}>
        {attorney?.map((order, index) => (
          <Card
            key={index}
            {...order}
            attornary={true}
            onDelete={() => handleDeleteAttorney(order.id)}
            onEdit={() => handleEditAttorney(order)}
          />
        ))}
      </div>

      <GlobalModalComponent
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
      >
        <div className={styles.deleteModal}>
          <div className={styles.close_icon}>
            <img
              onClick={() => setDeleteModalOpen(false)}
              src={CloseIcon}
              alt=""
            />
          </div>
          <p>{data?.auth_common_delete_attorney_confirm}</p>
          <div className={styles.buttons}>
            <MainButton
              text={data?.auth_logout_yes}
              type="button"
              onClick={handleConfirmDelete}
            />
            <MainButton
              text={data?.cancel}
              type="button"
              onClick={() => setDeleteModalOpen(false)}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </GlobalModalComponent>

      <GlobalModalComponent
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
      >
        <div className={styles.attornayEdit}>
          <div
            className={styles.close_icon}
            onClick={() => setEditModalOpen(false)}
          >
            <img src={CloseIcon} alt="" />
          </div>
          <form className={styles.account_purchase} onSubmit={handleEditSubmit}>
            <div className={styles.wrap_component}>
              <InputComponent
                label={data?.auth_attorney_name}
                type="text"
                name="name"
                placeholder={data?.auth_attorney_name}
                value={editFormValues.name}
                onChange={(e) =>
                  setEditFormValues({ ...editFormValues, name: e.target.value })
                }
                error={errors.name}
              />
            </div>
            <div className={styles.wrap_component}>
              <InputComponent
                label={data?.auth_attorney_surname}
                type="text"
                name="surname"
                placeholder={data?.auth_attorney_surname}
                value={editFormValues.surname}
                onChange={(e) =>
                  setEditFormValues({
                    ...editFormValues,
                    surname: e.target.value,
                  })
                }
                error={errors.surname}
              />
            </div>
            <div className={styles.wrap_component}>
              <InputComponent
                label={data?.auth_attorney_father_name}
                type="text"
                name="patronymic"
                placeholder={data?.auth_attorney_father_name}
                value={editFormValues.patronymic}
                onChange={(e) =>
                  setEditFormValues({
                    ...editFormValues,
                    patronymic: e.target.value,
                  })
                }
                error={errors.patronymic}
              />
            </div>
            <div className={styles.wrap_component}>
              <SelectComponent
                label={data?.auth_attorney_series_title}
                name="passport_series"
                options={[
                  { value: "AA", label: "AA" },
                  { value: "AZE", label: "AZE" },
                ]}
                selectedValue={editFormValues.passport_series}
                onChange={(value) => {
                  setEditFormValues({
                    ...editFormValues,
                    passport_series: String(value),
                  });
                }}
                placeholder={data?.auth_attorney_select}
                error={errors.passport_series}
              />
            </div>
            <div className={styles.wrap_component}>
              <InputComponent
                label={data?.auth_attorney_series_number}
                type="number"
                name="passport_number"
                placeholder="0000000"
                value={editFormValues.passport_number}
                onChange={(e) =>
                  setEditFormValues({
                    ...editFormValues,
                    passport_number: e.target.value,
                  })
                }
                error={errors.passport_number}
              />
            </div>
            <div className={styles.wrap_component}>
              <InputComponent
                label={data?.auth_attorney_passport_fin}
                type="text"
                name="passport_fin"
                placeholder={data?.auth_attorney_passport_fin}
                value={editFormValues.passport_fin}
                onChange={(e) =>
                  setEditFormValues({
                    ...editFormValues,
                    passport_fin: e.target.value,
                  })
                }
                error={errors.passport_fin}
              />
            </div>
            <div className={styles.wrap_component}>
              <p>{data?.auth_attorney_birth_date}</p>
              <div className={styles.dateInputWrapper}>
                <InputComponent
                  label=""
                  type="date"
                  name="birthday"
                  placeholder=""
                  value={editFormValues.birthday}
                  onChange={(e) =>
                    setEditFormValues({
                      ...editFormValues,
                      birthday: e.target.value,
                    })
                  }
                  error={errors.birthday}
                />
              </div>
            </div>
            <div className={styles.wrap_component}>
              <PhoneInputComponent
                onCodeChange={(code) =>
                  setEditFormValues({ ...editFormValues, phone_prefix: code })
                }
                label={data?.auth_attorney_mobile}
                id="phone"
                name="phone"
                placeholder="0000000"
                selectedCode={editFormValues.phone_prefix}
                value={editFormValues.phone}
                onChange={(e) =>
                  setEditFormValues((prevValues) => ({
                    ...prevValues,
                    phone: e.target.value,
                  }))
                }
                combinePrefixWithNumber={false}
                error={errors.phone}
              />
            </div>
            <div className={styles.wrap_component}>
              <MainButton
                className={styles.mainButton}
                text={data?.auth_attorney_confirm}
                type="submit"
              />
            </div>
          </form>
        </div>
      </GlobalModalComponent>
    </div>
  );
};

export default Attorney;
